import { useState, useEffect, useMemo } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import {
  CurrencyDollar, ArrowClockwise, PaperPlaneRight, TrendUp,
  Trophy, Users, Coin, CalendarBlank, FireSimple,
} from "@phosphor-icons/react";

/* Wave 16 · VA Bonus Tracker
   Founder-facing payroll dashboard. Renders each VA's monthly bonus in a
   leaderboard sorted by highest earner. Bonus formula = replies × base
   + signups × mid + paid × top. All numbers pulled live from
   /admin/va-bonus/summary + /admin/va-bonus/config. */

function _monthLabel(y, m) {
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function _money(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

export default function VABonusPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [historyByEmail, setHistoryByEmail] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [sumRes, histRes] = await Promise.all([
        client.get("/admin/va-bonus/summary", { params: { year, month } }),
        client.get("/admin/va-bonus/history", { params: { days: 30 } }),
      ]);
      setData(sumRes.data);
      const byEmail = {};
      for (const row of histRes.data?.rows || []) {
        byEmail[row.admin_email] = row;
      }
      setHistoryByEmail(byEmail);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load bonus data");
      toast.error("Failed to load bonus data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Recent months for the picker — 12 months back including current
  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({ y: d.getFullYear(), m: d.getMonth() + 1, label: _monthLabel(d.getFullYear(), d.getMonth() + 1) });
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const cfg = data?.config || {};

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="va-bonus-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="overline text-[#FF9764] mb-1.5 flex items-center gap-2">
            <CurrencyDollar size={12} weight="fill" /> PAYROLL · VA BONUS TRACKER
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white">
            {_monthLabel(year, month)}
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Auto-calculated from reply volume + attributed signups + paid conversions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="va-bonus-month-picker"
            value={`${year}-${month}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-").map(Number);
              setYear(y); setMonth(m);
            }}
            className="rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#FF5A1F] focus:outline-none px-3 py-2 text-sm text-white"
          >
            {monthOptions.map((o) => (
              <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>{o.label}</option>
            ))}
          </select>
          <button
            data-testid="va-bonus-refresh"
            onClick={load}
            disabled={loading}
            className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white transition-colors disabled:opacity-60"
          >
            <ArrowClockwise size={14} weight="bold" /> {loading ? "..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Config strip — the payroll formula, in plain English */}
      {cfg.per_reply_usd != null && (
        <div
          data-testid="va-bonus-formula-strip"
          className="rounded-xl border border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.04] p-4 mb-6 text-xs sm:text-sm text-neutral-300"
        >
          <span className="text-[#FF9764] font-semibold">Formula: </span>
          <span data-testid="va-bonus-formula-per-reply">{_money(cfg.per_reply_usd)}</span>
          <span className="text-neutral-500"> per reply × </span>
          <span data-testid="va-bonus-formula-quality-mult" className="text-emerald-300">quality multiplier</span>
          <span className="text-neutral-500"> ({cfg.quality_floor}x–{cfg.quality_ceil}x)</span>
          <span className="text-neutral-600 mx-2">+</span>
          <span data-testid="va-bonus-formula-per-signup">{_money(cfg.per_signup_usd)}</span>
          <span className="text-neutral-500"> per attributed signup</span>
          <span className="text-neutral-600 mx-2">+</span>
          <span data-testid="va-bonus-formula-per-paid">{_money(cfg.per_paid_usd)}</span>
          <span className="text-neutral-500"> per paid conversion</span>
          <div className="text-[11px] text-neutral-500 mt-1">
            Quality multiplier = avg reply score ÷ {cfg.quality_neutral_score} (clamped {cfg.quality_floor}x–{cfg.quality_ceil}x).
            Attribution window: {cfg.attribution_window_days} days after each reply.
          </div>
        </div>
      )}

      {/* Totals summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Replies sent" value={totals.reply_count ?? 0} icon={PaperPlaneRight} accent="text-neutral-200" testid="va-bonus-total-replies" />
        <StatCard label="Attributed signups" value={totals.signup_count ?? 0} icon={Users} accent="text-emerald-300" testid="va-bonus-total-signups" />
        <StatCard label="Paid conversions" value={totals.paid_count ?? 0} icon={TrendUp} accent="text-[#FF9764]" testid="va-bonus-total-paid" />
        <StatCard label="Total bonus this month" value={_money(totals.total_bonus_usd ?? 0)} icon={Coin} accent="text-[#FF5A1F]" testid="va-bonus-total-payout" />
      </div>

      {/* Leaderboard */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <div className="grid grid-cols-[1fr_repeat(6,minmax(72px,auto))] gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.02] text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
          <div>VA</div>
          <div className="text-right">Replies</div>
          <div className="text-right">Avg Q</div>
          <div className="text-right">Mult</div>
          <div className="text-right">Signups</div>
          <div className="text-right">Paid</div>
          <div className="text-right">Bonus</div>
        </div>
        {loading && rows.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500" data-testid="va-bonus-empty">
            {error || `No replies sent by any VA in ${_monthLabel(year, month)}. Come back after your VA has been active.`}
          </div>
        ) : (
          rows.map((r, i) => (
            <div
              key={r.admin_email}
              data-testid={`va-bonus-row-${r.admin_email}`}
              className="grid grid-cols-[1fr_repeat(6,minmax(72px,auto))] gap-2 px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                {i === 0 && rows.length > 1 && <Trophy size={14} weight="fill" className="text-yellow-400 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-medium truncate flex items-center gap-1.5">
                    {r.admin_name}
                    {r.high_q_streak >= 3 && (
                      <span
                        data-testid={`va-bonus-streak-badge-${r.admin_email}`}
                        title={`${r.high_q_streak} replies in a row at ${r.high_q_streak_threshold}+ quality`}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 shrink-0"
                      >
                        <FireSimple size={9} weight="fill" /> {r.high_q_streak}× {r.high_q_streak_threshold}+
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 truncate">{r.admin_email}</div>
                  <QualitySparkline
                    history={historyByEmail[r.admin_email]}
                    streakThreshold={r.high_q_streak_threshold || 80}
                    testid={`va-bonus-sparkline-${r.admin_email}`}
                  />
                </div>
              </div>
              <div className="text-right font-mono text-sm text-neutral-200">{r.reply_count}</div>
              <div className={`text-right font-mono text-sm ${
                r.avg_quality_score >= 70 ? "text-emerald-300"
                : r.avg_quality_score >= 50 ? "text-amber-300"
                : "text-red-300"
              }`}>{r.avg_quality_score || "—"}</div>
              <div className={`text-right font-mono text-xs ${
                r.quality_multiplier >= 1 ? "text-emerald-300" : "text-red-300"
              }`}>{r.quality_multiplier}x</div>
              <div className="text-right font-mono text-sm text-emerald-300">{r.signup_count}</div>
              <div className="text-right font-mono text-sm text-[#FF9764]">{r.paid_count}</div>
              <div className="text-right font-mono text-sm text-[#FF5A1F] font-semibold">
                {_money(r.total_bonus_usd)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footnote */}
      <div className="mt-6 text-[11px] text-neutral-600 leading-relaxed max-w-2xl flex items-start gap-2">
        <CalendarBlank size={12} weight="bold" className="mt-0.5 shrink-0" />
        <span>
          Attribution: a reply's <code className="text-neutral-500">lead_email</code> is matched against
          users signed up within {cfg.attribution_window_days || 30} days after the reply. Paid
          conversions require a non-trial subscription status. Rates are configurable via
          <code className="text-neutral-500 mx-1">VA_BONUS_PER_*_USD</code>
          env vars in <code className="text-neutral-500">backend/.env</code>.
        </span>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="overline text-neutral-500">{label}</div>
        <Icon size={13} weight="fill" className={accent} />
      </div>
      <div className={`font-mono text-2xl font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

// Inline SVG sparkline of the last 30 days of daily quality scores per VA.
// Dependency-free (no chart lib) so the Bonus page stays fast + light.
// Each daily bar coloured by its avg quality band (green ≥80, amber ≥50,
// red <50, ghost-grey for silent days). A dotted horizontal line marks
// the streak threshold so the "how close was this day to a streak?"
// answer is visible at a glance.
function QualitySparkline({ history, streakThreshold = 80, testid }) {
  if (!history || !Array.isArray(history.days) || history.days.length === 0) {
    return null;
  }
  const days = history.days;
  const width = 200;
  const height = 32;
  const barW = width / days.length;
  const gap = Math.max(1, barW * 0.15);

  // Y-axis: 0-100 quality → 0-height pixels
  const y = (score) => height - (score / 100) * height;
  const thresholdY = y(streakThreshold);

  return (
    <div className="mt-1.5" data-testid={testid}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily quality history for the last ${days.length} days`}
      >
        {/* Streak-threshold line — dotted, subtle */}
        <line
          x1="0" x2={width}
          y1={thresholdY} y2={thresholdY}
          stroke="rgba(16, 185, 129, 0.28)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />
        {days.map((d, idx) => {
          const x = idx * barW + gap / 2;
          const w = barW - gap;
          const q = d.avg_quality;
          if (q == null) {
            // Silent day — thin grey tick at the bottom so gaps stay visible
            return (
              <rect
                key={d.date}
                x={x} y={height - 1.5} width={w} height={1.5}
                fill="rgba(115, 115, 115, 0.3)"
              />
            );
          }
          const fill = q >= 80 ? "#10B981"     // emerald
                     : q >= 50 ? "#F59E0B"     // amber
                     : "#EF4444";              // red
          const barY = y(q);
          const barH = Math.max(1.5, height - barY);
          return (
            <g key={d.date}>
              <rect x={x} y={barY} width={w} height={barH} fill={fill} rx="0.5">
                <title>{`${d.date} · ${q}/100 · ${d.reply_count} ${d.reply_count === 1 ? "reply" : "replies"} · streak EOD ${d.streak_at_eod}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center justify-between text-[9px] text-neutral-600 mt-0.5">
        <span>{days[0].date.slice(5)}</span>
        <span>peak streak {history.peak_streak_window || 0} · {history.total_replies_window} replies</span>
        <span>{days[days.length - 1].date.slice(5)}</span>
      </div>
    </div>
  );
}

